import React from 'react'
import { Code, Layers, Zap, Users } from 'lucide-react'

export const AboutPage: React.FC = () => {
  const principles = [
    {
      icon: Code,
      title: 'Clean Architecture',
      description: 'Modular, maintainable code structure with clear separation of concerns and comprehensive TypeScript support.'
    },
    {
      icon: Layers,
      title: 'Extensible Design',
      description: 'Built with extensibility in mind. Easy to add new features, integrate with APIs, and customize for specific needs.'
    },
    {
      icon: Zap,
      title: 'Performance First',
      description: 'Optimized for speed with modern build tools, efficient state management, and lazy loading strategies.'
    },
    {
      icon: Users,
      title: 'Developer Experience',
      description: 'Complete development toolchain with hot reload, linting, type checking, and comprehensive documentation.'
    }
  ]

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">
          About KGAutomix Minisite
        </h1>
        <p className="text-xl text-gray-600 leading-relaxed">
          A showcase of modern web audio capabilities, built as a versatile foundation 
          for audio-centric applications.
        </p>
      </div>

      {/* Overview */}
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Project Overview</h2>
        <div className="prose prose-gray max-w-none">
          <p className="text-gray-700 leading-relaxed mb-4">
            This demo application demonstrates the integration of WaveSurfer.js with a modern 
            React + TypeScript frontend and Express.js backend. It serves as both a functional 
            audio player and a template for building more complex audio applications.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            The project is designed with extensibility as a core principle. Whether you're 
            building a music streaming service, podcast platform, or audio analysis tool, 
            this foundation provides the essential components to get started quickly.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Every component is thoroughly documented and follows modern best practices for 
            maintainability, performance, and developer experience.
          </p>
        </div>
      </div>

      {/* Design Principles */}
      <div className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-8 text-center">
          Design Principles
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {principles.map((principle, index) => {
            const Icon = principle.icon
            return (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-3 rounded-lg flex-shrink-0">
                    <Icon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {principle.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {principle.description}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Technical Details */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-8 mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Technical Implementation</h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Frontend Stack</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                <span><strong>React 18:</strong> Concurrent features & improved performance</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                <span><strong>TypeScript:</strong> Type safety & enhanced developer experience</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                <span><strong>Vite:</strong> Lightning-fast development & optimized builds</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                <span><strong>Tailwind CSS:</strong> Utility-first styling approach</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Backend & Tools</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span><strong>Express.js:</strong> Robust API server with middleware support</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span><strong>Node.js:</strong> Server-side JavaScript runtime</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span><strong>ESLint + Prettier:</strong> Code quality & formatting</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span><strong>Concurrently:</strong> Development workflow automation</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Key Features</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Current Features</h3>
            <ul className="space-y-2 text-gray-700">
              <li>• High-quality audio waveform visualization</li>
              <li>• Responsive design for all devices</li>
              <li>• Real-time playback controls</li>
              <li>• Audio file streaming with range support</li>
              <li>• REST API for audio management</li>
              <li>• TypeScript type safety throughout</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Extension Points</h3>
            <ul className="space-y-2 text-gray-700">
              <li>• User authentication & preferences</li>
              <li>• Playlist management</li>
              <li>• Audio effects & processing</li>
              <li>• Real-time collaboration features</li>
              <li>• Database integration</li>
              <li>• Advanced analytics & metrics</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
